import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { get } from 'lodash';
import {
  Answers,
  IBmiAnswerConfig,
  IFile,
  IQuestionModel
} from '@care-portals/api-interfaces';
import { calculateBMI, formatHeight } from '@care-portals/data';

@Component({
  selector: 'consultation-answer',
  templateUrl: './consultation-answer.component.html',
  styleUrls: ['./consultation-answer.component.less']
})
export class ConsultationAnswerComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() data;
  @Input() field: string;
  @Input() answer;
  @Input() answers: Answers;
  @Input() brand: string;
  @Input() question: IQuestionModel;
  @Input() allAnswers: any;

  optionAnswers = {};
  hideLabel = false;
  transferRxFileName: string;
  transferRxFile: IFile;
  customerId: string;
  active = true;
  now = new Date();

  // files
  isModalOpen: boolean = false;
  selectedImage: string = '';
  files: { name: string; path: string; loading: boolean }[];

  constructor() {}

  ngOnInit(): void {
    if (this.answer.type === 'radio') {
      this.answer.options.map(option => {
        if (option.answer) {
          this.optionAnswers[option.key] = option.answer;
        }
      });
    }

    if (this.answer.type === 'esign') {
      const questionKey = this.question.field;
      const questionAnswer = this.allAnswers[questionKey];
      let path = questionAnswer[this.answer.name];
      // fix for backwards compatibility on some older consultations
      if (typeof path !== 'string') {
        path = questionAnswer[this.answer.name][this.answer.name];
      }
      if (path) {
        this.files = [
          {
            name: this.answer.name,
            loading: true,
            path: path.replace('esign/', '')
          }
        ];
      }
    }

    if (this.question?.type === 'file_upload') {
      this.files = (this.question.answer || []).map(a => ({
        name: a.resourceId,
        path:
          typeof this.allAnswers[a.resourceId] === 'string'
            ? this.allAnswers[a.resourceId]
            : this.allAnswers[a.resourceId][a.resourceId],
        loading: true
      }));
    }

    if (this.question?.type === 'image_list') {
      const resourceId = this.question?.answer?.option?.resourceId;
      if (resourceId) {
        const keys = Object.keys(this.allAnswers).filter(
          key => key.startsWith(resourceId) && /\d$/.test(key)
        );
        this.files = (keys || []).map(key => ({
          name: key,
          path:
            typeof this.allAnswers[key] === 'string'
              ? this.allAnswers[key]
              : this.allAnswers[key][key],
          loading: true
        }));
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      if (this.data === undefined) {
        this.data = {};
      }
    }
  }

  ngOnDestroy(): void {
    this.active = false;
  }

  get answerName() {
    let defaultName = 'defaultName';

    if (this.answer.type === 'text') defaultName = 'description';
    if (this.answer.type === 'number') defaultName = 'value';
    if (this.answer.type === 'date') defaultName = 'date';
    if (this.answer.type === 'consent-check') defaultName = 'accept';

    return this.answer.name || defaultName;
  }

  get isAccept() {
    return !this.answer.required || this.data['accept'] ? 'true' : '';
  }

  get uncheckAnswer() {
    const option =
      this.answer.type === 'multi_select'
        ? this.answer.options.filter(
            (opt: { key: string; value: string; uncheckOthers?: boolean }) =>
              opt.uncheckOthers
          )[0]
        : null;

    return option ? option.key : null;
  }

  get brandName() {
    const brand = this.brand?.split('-')[0];
    return brand?.charAt(0).toUpperCase() + brand?.slice(1);
  }

  get bmiConfig(): IBmiAnswerConfig {
    return this.answer || {};
  }

  get bmiValue(): string {
    return (calculateBMI(this.data || {}) ?? '').toString();
  }

  get bmiHeightValue(): string {
    return formatHeight(this.data || {});
  }

  get bmiWeightValue(): string {
    const { weight, weightUnit } = this.data || {};
    return (weight || '') + (weightUnit ? ` ${weightUnit}` : '');
  }

  checkCondition(cond: { key: string; operator: 'eq' | 'ne'; value: string }) {
    const { key, operator, value } = cond;
    const v = get(this.allAnswers, key, '');
    if (operator === 'eq' && v === value) {
      return true;
    } else if (operator === 'ne' && v !== value) {
      return true;
    }
    return false;
  }

  get measureAnswerValue() {
    const selectedUnit = this.data[this.answer.unit.name];
    return !this.answer.value.placeholder
      ? {
          ...this.answer.value,
          placeholder:
            selectedUnit === 'feetIn'
              ? 'feet'
              : selectedUnit
              ? selectedUnit
              : this.answer.value.name
        }
      : this.answer.value;
  }

  openModal(image: string) {
    this.selectedImage = image;
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onLoad(name: string) {
    const file = this.files?.find(f => f.name == name);
    if (file) {
      file.loading = false;
    }
  }
}
